// Serviço de pendências do usuário: busca, na API de Pendência e Notificação,
// os documentos indeferidos ainda em aberto do usuário logado
// (GET /pendencias/{pessoaId}).

import pendenciaApi from '../../../services/pendenciaApi';
import { authService, protocoloService } from '../../../services';
import { getPedidoPorModeloRequerimento } from '../../requerimentos/config';
import { MODELO_REQUERIMENTO_INSCRITO, MODELO_REQUERIMENTO_NAO_INSCRITO, TIPOS_ASSUNTO_PROTOCOLO_ONLINE } from '../../protocolo/services/protocoloService';
import { TIPO_DOCUMENTO_REQUERIMENTO, TIPO_DOCUMENTO_ADICIONAL, TIPO_DOCUMENTO_ADICIONAL_LEGADO } from '../../protocolo/services/documentoAnexoService';
import logger from '../../../utils/logger';

const formatarData = (isoDate) => {
  if (!isoDate) return '—';
  const data = new Date(isoDate);
  return Number.isNaN(data.getTime()) ? '—' : data.toLocaleDateString('pt-BR');
};

// Documento indeferido dentro do pedido resolvido (required + conditional +
// optional), casando pelo mesmo ID que a api-anexo-ged usa como
// tipoDocumentoProcesso — só pra exibir o nome do documento na tela, sem
// precisar de outra API.
const resolverDocumento = (config, tipoDocumentoId) => {
  if (!config?.documentConfig || tipoDocumentoId == null) return null;

  const todos = [
    ...(config.documentConfig.required || []),
    ...(config.documentConfig.conditional || []),
    ...(config.documentConfig.optional || [])
  ];

  return todos.find((doc) => Number(doc.tipoDocumentoProcesso) === Number(tipoDocumentoId)) || null;
};

// Protocolo Online não tem documentConfig de verdade (o tipo de documento é
// escolhido livremente na hora do upload, não vem de uma lista fixa do
// pedido) — só os tipos fixos que documentoAnexoService.js usa (mais o tipo
// legado dos anexos opcionais, pra pendências de protocolos antigos).
// Modelado como "config" pra reaproveitar resolverDocumento() sem duplicar a
// lógica de busca.
const CONFIG_PROTOCOLO_ONLINE = {
  documentConfig: {
    required: [
      { tipoDocumentoProcesso: TIPO_DOCUMENTO_REQUERIMENTO, name: 'Requerimento' },
      { tipoDocumentoProcesso: TIPO_DOCUMENTO_ADICIONAL, name: 'Documento adicional' },
      { tipoDocumentoProcesso: TIPO_DOCUMENTO_ADICIONAL_LEGADO, name: 'Documento adicional' }
    ]
  }
};

// Protocolo Online (modules/protocolo) não faz parte do catálogo de
// departamentos/pedidos do wizard de Requerimentos (modules/requerimentos/config)
// — é um fluxo à parte, com modeloRequerimento fixo (221 inscrito / 258 não
// inscrito) e tiposAssunto fixo.
const resolverProtocoloOnline = (modeloRequerimentoId) => {
  const modelo = String(modeloRequerimentoId);
  if (modelo !== MODELO_REQUERIMENTO_INSCRITO && modelo !== MODELO_REQUERIMENTO_NAO_INSCRITO) return null;

  return { nome: 'Protocolo Online', tiposAssunto: TIPOS_ASSUNTO_PROTOCOLO_ONLINE, config: CONFIG_PROTOCOLO_ONLINE };
};

const resolverPedido = (modeloRequerimentoId) =>
  getPedidoPorModeloRequerimento(modeloRequerimentoId) || resolverProtocoloOnline(modeloRequerimentoId);

export const pendenciasService = {
  async getPendencias() {
    let pessoaId;

    try {
      const user = authService.getCurrentUser();
      ({ pessoaId } = protocoloService.extrairIdsUsuario(user));
    } catch (error) {
      return { success: false, error: 'Não foi possível identificar o usuário logado.' };
    }

    try {
      const response = await pendenciaApi.get(`/pendencias/${pessoaId}`);
      const registros = response.data?.data || [];

      const pendencias = registros.map((item) => {
        const pedido = resolverPedido(item.modeloRequerimentoId);
        const documento = resolverDocumento(pedido?.config, item.tipoDocumentoId);

        return {
          pendenciaId: item.pendenciaId,
          requerimentoId: item.requerimentoId,
          tipoDocumentoId: item.tipoDocumentoId,
          // protocoloId é o ID da linha em DocumentoProtocolo (vai no
          // /reenviar, ver documentoReenvioService); pra tela, o número do
          // protocolo que a pessoa reconhece é numeroProtocolo.
          protocoloId: item.protocoloId,
          numeroProtocolo: item.numeroProtocolo ?? '—',
          modeloRequerimentoId: item.modeloRequerimentoId,
          documentoEntregueId: item.documentoEntregueId,
          motivoIndeferimento: item.motivoIndeferimento || 'Motivo não informado pela seccional',
          numeroRequerimento: item.numeroRequerimento ?? '—',
          dataIndeferimento: formatarData(item.dataSolucaoPendencia),
          // Resolvidos via catálogo de pedidos do portal (config/index.js), a
          // partir do modeloRequerimentoId — null quando o modelo não bate com
          // nenhum pedido conhecido (catálogo desatualizado ou modelo legado
          // fora do portal).
          nomePedido: pedido?.nome || null,
          departamentoId: pedido?.departamentoId || null,
          pedidoId: pedido?.pedidoId || null,
          tiposAssunto: pedido?.tiposAssunto || null,
          nomeDocumento: documento?.name || null
        };
      });

      return { success: true, data: pendencias };
    } catch (error) {
      logger.error('Erro ao listar pendências:', error);
      const mensagem = error.response?.data?.message || error.response?.data?.error || 'Erro ao carregar pendências';
      return { success: false, error: mensagem };
    }
  }
};
